import React, { useContext, useEffect, useState } from 'react';

import { useFormContext ,  Controller } from 'react-hook-form';
import { Form, FormGroup } from 'react-bootstrap';
import Select, { ValueType } from 'react-select';
import { Link } from 'react-router-dom';

import { useFetchData } from '../../../../../../infrastructure/hooks/useFetchData';
import { VendorType, type BookingPlatformSupplier } from '../../../../../../types/Vendor';
import * as VendorsService from '../../../../../../services/VendorsService';
import * as ActivitiesService from '../../../../../../services/ActivitiesService';
import { ApiStatus } from '../../../../../../infrastructure/constants/apiStatus';
import type { CustomerPrototype, FareharborItem } from '../../../../../../types/Fareharbor';
import { ActivityContext } from '../../../../../../contexts/Activity';
import { isNotUndefined } from '../../../../../../infrastructure/utils/common';


export type FHReferenceFormChanges = {
  itemId: string;
  customerPrototypeId: string;
}

export type FHReferenceFormInputProps = {
  itemId?: string;
  customerPrototypeId?: string;
};
type Props = FHReferenceFormInputProps & {
  onApplyChanges: (data: FHReferenceFormChanges) => void;
}

type RemoteData = {
  suppliers: BookingPlatformSupplier[];
  items: FareharborItem[];
};

type FormData = {
  supplier?: ValueType<BookingPlatformSupplier, false>;
  item?: ValueType<FareharborItem, false>;
  customerPrototype?: ValueType<CustomerPrototype, false>;
}
type ValidFormData = {
  supplier: BookingPlatformSupplier;
  item: FareharborItem;
  customerPrototype: CustomerPrototype;
};

const FHReferenceForm = (props:Props) => {
  const form = useFormContext<FormData, undefined, ValidFormData>();
  const selectedSupplier = form.watch('supplier');
  const selectedItem = form.watch('item');
  const selectedCustomerPrototype = form.watch('customerPrototype');

  const { vendor, bookingOptions } = useContext(ActivityContext);
  const [apiStatus, remoteData, fetchRemoteData] = useFetchData<RemoteData>();

  const [vendorSuppliers, setVendorSuppliers] = useState<
    BookingPlatformSupplier[]
  >([]);
  const [vendorItems, setVendorItems] = useState<FareharborItem[]>([]);
  const [customerPrototypes, setCustomerPrototypes] = useState<CustomerPrototype[]>([]);

  useEffect(() => {
    fetchRemoteData(async () => {
      const [suppliers, items] = await Promise.all([
        VendorsService.getSuppliers(VendorType.fareharbor),
        ActivitiesService.getFHItems(),
      ]);      

      return { suppliers, items };
    });
  }, []);

  useEffect(() => {
    if (apiStatus !== ApiStatus.success) {
      return;
    }

    if(!remoteData){
      setVendorItems([]);
      setVendorSuppliers([]);
      return;
    }

    const { suppliers, items } = remoteData;
    
    const suppliersMap = new Map(suppliers.map(supplier => [supplier.id, supplier]));

    // Allow to select only the suppliers that are configured for the vendor
    const vendorSuppliers = (vendor?.bookingPlatforms || []).filter(p => p.type === VendorType.fareharbor).map(p => suppliersMap.get(p.supplierId)).filter(isNotUndefined).toSorted((a,b) => a.name.localeCompare(b.name));
    setVendorSuppliers(vendorSuppliers);

    const selectedItem = items.find(item => item.pk.toString() === props.itemId);
    if(!selectedItem){
      form.setError('item', {message: `Item [${props.itemId}] not found`});
      return;
    }
    if(selectedItem.deletedAt){
      form.setError('item', {message: `Item [${selectedItem.pk}] — ${selectedItem.name} is deleted at ${selectedItem.deletedAt}`});
      //do not break the flow because we want to try to set supplier.
    }

    const supplierId = selectedItem?.company_shortname;
    const selectedSupplier = suppliersMap.get(supplierId || '');
    if(!selectedSupplier){
      form.setError('supplier', {message: `Supplier [${supplierId}] not found`});
      return;
    }
    if(vendorSuppliers.every(s => s.id !== supplierId)){
      form.setError('supplier', {type: `SupplierNotConfigured`, message: supplierId});
      return;
    }
    form.setValue('supplier', selectedSupplier);   

    if(selectedItem.deletedAt){
      //if item is deleted we now can break the flow      
      return;
    }

    form.setValue('item', selectedItem);

    const customerPrototypes = selectedItem?.customer_prototypes || [];
    const selectedCustomerPrototype = customerPrototypes.find(cp => cp.pk.toString() === props.customerPrototypeId);
    if(!selectedCustomerPrototype){
      form.setError('customerPrototype', {message: `Customer Prototype [${props.customerPrototypeId}] not found`});
      return;
    }
    form.setValue('customerPrototype', selectedCustomerPrototype);
  }, [apiStatus, remoteData])

  useEffect(() => {
    if(!selectedSupplier){
      setVendorItems([]);
      form.setValue('item', null);
      return;
    }

    const items = (remoteData?.items || []).filter(item => item.company_shortname === selectedSupplier.id && !item.deletedAt).toSorted((a,b) => a.name.localeCompare(b.name));
    setVendorItems(items);
    if(form.getFieldState('supplier').isDirty){
      form.setValue('item', null);
    }
  },[selectedSupplier]);

  useEffect(() => {
    if(!selectedItem){
      setCustomerPrototypes([]);
      form.setValue('customerPrototype', null);
      return;
    }

    const customerPrototypes = (selectedItem?.customer_prototypes || []).toSorted((a,b) => a.display_name.localeCompare(b.display_name));
    setCustomerPrototypes(customerPrototypes);
    if(form.getFieldState('item').isDirty){
      form.setValue('customerPrototype', null);
    }
  },[selectedItem]);

  useEffect(() => {
    const selectedId = selectedCustomerPrototype?.pk.toString();
    if(!selectedId){
      return;
    }
    form.clearErrors('customerPrototype');

    if(selectedId === props.customerPrototypeId){
      return;
    }

    const referenceExists = bookingOptions?.some(opt => opt.id === selectedId);
    if(referenceExists){
      form.setError('customerPrototype', {message: `Selected customer prototype is already referenced in another booking option`});
      return;
    }       
  },[selectedCustomerPrototype]);

  const handleSubmit = (data: ValidFormData) => {
    props.onApplyChanges({
      itemId: data.item.pk.toString(),
      customerPrototypeId: data.customerPrototype.pk.toString()
    });
  }

  const renderSupplierError = () => {
    if(!form.formState.errors.supplier) return null;

    if(form.formState.errors.supplier.type === 'SupplierNotConfigured'){
      return <small className='text-danger'>
        Supplier [{form.formState.errors.supplier.message}] is not configured for the vendor. Please add it to the <Link to={`/vendors/${vendor?.type}/${vendor?.id}`}>vendor details</Link>.
      </small>
    }

    return <small className='text-danger'>{form.formState.errors.supplier.message}</small>
  }

  return (
    <Form id='fhReferenceForm' onSubmit={form.handleSubmit(handleSubmit)}>
      <FormGroup>
        <p>Supplier</p>
        <Controller name='supplier' control={form.control} rules={{required: {value:true, message: 'Supplier is required'}}} render={({field}) => (
          <Select {...field} options={vendorSuppliers} getOptionLabel={(option) => `[${option.id}] — ${option.name}`} getOptionValue={(option) => option.id} isLoading={apiStatus === ApiStatus.loading}/>
        )} />
        {renderSupplierError()}
      </FormGroup>
      <FormGroup>
        <p>Item</p>
        <Controller name='item' control={form.control} rules={{required: {value:true, message: 'Item is required'}}} render={({field}) => (
          <Select {...field} options={vendorItems} getOptionLabel={(option) => `[${option.pk}] — ${option.name}`} getOptionValue={(option) => option.pk.toString()} isLoading={apiStatus === ApiStatus.loading}/>
        )} />
        {form.formState.errors.item && <small className='text-danger'>{form.formState.errors.item.message}</small>}
      </FormGroup>
      <FormGroup>
        <p>Customer Prototype</p>
        <Controller name='customerPrototype' control={form.control} rules={{required: {value:true, message: 'Customer Prototype is required'}}} render={({field}) => (
          <Select {...field} options={customerPrototypes} getOptionLabel={(option) => `[${option.pk}] — ${option.display_name}`} getOptionValue={(option) => option.pk.toString()} isLoading={apiStatus === ApiStatus.loading}/>
        )} />
        {form.formState.errors.customerPrototype && <small className='text-danger'>{form.formState.errors.customerPrototype.message}</small>}
      </FormGroup>
    </Form>
  )
};

export default FHReferenceForm;
